import React, { FC, memo, useMemo } from 'react';
import InputError from '@components/inputs/InputError';
import InputLabel from '@components/inputs/InputLabel';
import FieldController from '@components/form/FieldController';
import { FieldComponentProps } from '@/types';
import { IMaskInput } from 'react-imask';

interface Props extends FieldComponentProps {
  className?: string;
  maxLabelInput?: boolean;
  accordion?: boolean;
  mapToRadix?: string[];
  defaultValue?: number;
}

const numericProps: Record<string, Record<string, any>> = {
  integer: {
    thousandsSeparator: '',
  },
};

const ControlledNumericInput: FC<Props> = ({
  className,
  mapToRadix,
  maxLabelInput,
  accordion,
  defaultValue,
  ...props
}) => {
  const { type, name, autoFocus, max_width, ...q } = props;
  const range = q.max ? `${q.min ?? 0}-${q.max}` : undefined;
  const onlyMin = q.min;
  const placeholder = q.placeholder ?? range ?? onlyMin ?? '';
  const style = useMemo(() => ({ ...(max_width && { maxWidth: max_width }) }), [max_width, max_width]);

  return (
    <FieldController
      name={`${q.model}.${name}`}
      q={q}
      type={type}
      defaultValue={defaultValue}
      render={({ field: { onChange, ref, ...field }, fieldState: { error } }) => {
        return (
          <div>
            <InputLabel
              inputStyle={style}
              maxLabelInput={maxLabelInput ? maxLabelInput : false}
              className={className}
              q={props}
              accordion={accordion ? accordion : false}
            >
              <IMaskInput
                autoFocus={autoFocus}
                radix=","
                mapToRadix={mapToRadix ?? ['.']}
                mask={Number}
                thousandsSeparator="."
                unmask={true}
                onAccept={(value: string) => onChange(value)}
                {...(error && { style: { borderColor: 'var(--input-selected-error) !important' } })}
                className="w-full rounded border border-base-300 p-3 mt-2 text-neutral-800"
                placeholder={placeholder.toString()}
                pattern={q.pattern}
                padFractionalZeros={false}
                normalizeZeros={false}
                {...field}
                value={String(field.value)}
                inputRef={ref}
                {...numericProps[type]}
              />
              {q.unit && (
                <span className="absolute right-2 mt-[18px]" style={{ color: 'var(--text-accent) !important' }}>
                  {q.unit}
                </span>
              )}
            </InputLabel>
            <div className="mt-2 mb-2">{error && <InputError error={error.message} />}</div>
          </div>
        );
      }}
    />
  );
};

export default memo(ControlledNumericInput);
